// Property data used for LodgingBusiness JSON-LD schema on /book-now/{property} pages.
// Geo coordinates are approximate (River Road / Canyon Lake corridor near New Braunfels, TX).

export interface PropertyInfo {
  slug: string;
  name: string;
  description: string;
  url: string;
  priceRange: string;
  amenities: string[];
  geo: { lat: number; lng: number };
  address?: { street?: string; city: string; region: string; postal?: string };
  image?: string;
}

const COMMON_ADDRESS = {
  city: "New Braunfels",
  region: "TX",
  postal: "78132",
};

export const PROPERTIES: Record<string, PropertyInfo> = {
  "sons-guadalupe": {
    slug: "sons-guadalupe",
    name: "Son's Guadalupe — Day Rentals",
    description:
      "Day cabana rentals, in-water picnic tables, and tubing on the Guadalupe River in New Braunfels, TX. 22 private cabanas with grills, parking and direct river access.",
    url: "https://sonsguadalupe.com/book-now/sons-guadalupe",
    priceRange: "$69-$899",
    amenities: ["Private river access", "Charcoal grill", "Picnic table", "Dedicated parking", "Restrooms", "Wi-Fi"],
    geo: { lat: 29.8694, lng: -98.1972 },
    address: { ...COMMON_ADDRESS, street: "7308 River Rd" },
  },
  "sons-guadalupe-overnight": {
    slug: "sons-guadalupe-overnight",
    name: "Son's Guadalupe — Glamping & Overnight",
    description:
      "Overnight glamping bell tents on the Guadalupe River in New Braunfels, TX. AC, queen beds, electricity, river access and tubing.",
    url: "https://sonsguadalupe.com/book-now/sons-guadalupe-overnight",
    priceRange: "$129-$399",
    amenities: ["Air conditioning", "Queen bed", "Electricity", "River access", "Bathhouses", "Fire pit"],
    geo: { lat: 29.8694, lng: -98.1972 },
    address: { ...COMMON_ADDRESS, street: "7308 River Rd" },
  },
  "river-ranch": {
    slug: "river-ranch",
    name: "Son's River Ranch — Day Rentals",
    description:
      "Day rentals at Son's River Ranch on the Guadalupe River — cabanas, picnic tables, river access and tubing in New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/river-ranch",
    priceRange: "$69-$799",
    amenities: ["River access", "Cabanas", "Picnic tables", "Parking", "Restrooms"],
    geo: { lat: 29.8675, lng: -98.1855 },
    address: COMMON_ADDRESS,
  },
  "river-ranch-overnight": {
    slug: "river-ranch-overnight",
    name: "Son's River Ranch — Overnight Glamping",
    description:
      "Overnight glamping and tent camping at Son's River Ranch on the Guadalupe River near New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/river-ranch-overnight",
    priceRange: "$99-$399",
    amenities: ["Glamping tents", "Tent camping", "River access", "Fire pits", "Bathhouses"],
    geo: { lat: 29.8675, lng: -98.1855 },
    address: COMMON_ADDRESS,
  },
  "blue-river-camp": {
    slug: "blue-river-camp",
    name: "Son's Blue River Camp — Day Rentals",
    description:
      "Day cabana and picnic rentals at Son's Blue River Camp on the Guadalupe River near New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/blue-river-camp",
    priceRange: "$69-$799",
    amenities: ["River access", "Cabanas", "Picnic tables", "Parking", "Tubing"],
    geo: { lat: 29.8788, lng: -98.2305 },
    address: COMMON_ADDRESS,
  },
  "blue-river-camp-overnight": {
    slug: "blue-river-camp-overnight",
    name: "Son's Blue River Camp — Overnight Glamping",
    description:
      "Overnight glamping at Son's Blue River Camp on the Guadalupe River near New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/blue-river-camp-overnight",
    priceRange: "$69-$399",
    amenities: ["Glamping tents", "AC", "River access", "Fire pits"],
    geo: { lat: 29.8788, lng: -98.2305 },
    address: COMMON_ADDRESS,
  },
  "rio-cibolo": {
    slug: "rio-cibolo",
    name: "Son's Rio Cibolo — Day Rentals",
    description:
      "Day cabana rentals on Cibolo Creek at Son's Rio Cibolo Ranch near Marion / New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/rio-cibolo",
    priceRange: "$69-$799",
    amenities: ["Creek access", "Cabanas", "Picnic tables", "Parking"],
    geo: { lat: 29.5688, lng: -98.1366 },
    address: { city: "Marion", region: "TX", postal: "78124" },
  },
  "rio-cibolo-overnight": {
    slug: "rio-cibolo-overnight",
    name: "Son's Rio Cibolo — Overnight Glamping",
    description:
      "Overnight glamping at Son's Rio Cibolo on Cibolo Creek near Marion / New Braunfels, TX.",
    url: "https://sonsguadalupe.com/book-now/rio-cibolo-overnight",
    priceRange: "$99-$399",
    amenities: ["Glamping tents", "AC", "Creek access", "Fire pits"],
    geo: { lat: 29.5688, lng: -98.1366 },
    address: { city: "Marion", region: "TX", postal: "78124" },
  },
  "sons-island": {
    slug: "sons-island",
    name: "Son's Island — Day Rentals",
    description:
      "Day rentals on a private spring-fed island at Son's Island in Seguin, TX — cabanas and lakeside picnic tables.",
    url: "https://sonsguadalupe.com/book-now/sons-island",
    priceRange: "$69-$899",
    amenities: ["Lake access", "Cabanas", "Picnic tables", "Swimming"],
    geo: { lat: 29.5876, lng: -97.9647 },
    address: { city: "Seguin", region: "TX", postal: "78155" },
  },
  "sons-island-overnight": {
    slug: "sons-island-overnight",
    name: "Son's Island — Overnight Glamping",
    description:
      "Overnight glamping on a private spring-fed lake at Son's Island in Seguin, TX.",
    url: "https://sonsguadalupe.com/book-now/sons-island-overnight",
    priceRange: "$129-$399",
    amenities: ["Glamping tents", "AC", "Lake access", "Fire pits"],
    geo: { lat: 29.5876, lng: -97.9647 },
    address: { city: "Seguin", region: "TX", postal: "78155" },
  },
  geronimo: {
    slug: "geronimo",
    name: "Son's Geronimo — Day Rentals",
    description:
      "Day rentals at Son's Geronimo on Geronimo Creek in Seguin, TX — cabanas, creek access and picnic tables.",
    url: "https://sonsguadalupe.com/book-now/geronimo",
    priceRange: "$69-$799",
    amenities: ["Creek access", "Cabanas", "Picnic tables", "Parking"],
    geo: { lat: 29.6033, lng: -97.9986 },
    address: { city: "Seguin", region: "TX", postal: "78155" },
  },
};

const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export const getBookingPageSchema = (slug: string) => {
  const lodging = lodgingSchemaForProperty(slug);
  const p = PROPERTIES[slug];
  const breadcrumbs = breadcrumbSchema([
    { name: "Home", url: "https://sonsguadalupe.com/" },
    { name: "Book Now", url: "https://sonsguadalupe.com/book-now" },
    { name: p?.name || "Property", url: p?.url || `https://sonsguadalupe.com/book-now/${slug}` },
  ]);
  return lodging ? [lodging, breadcrumbs] : [breadcrumbs];
};

export const lodgingSchemaForProperty = (slug: string) => {
  const p = PROPERTIES[slug];
  if (!p) return null;
  return {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: p.name,
    description: p.description,
    url: p.url,
    image: p.image || "https://sonsguadalupe.com/og-image.png",
    telephone: "+1-877-577-7667",
    email: "info@sonsgetaways.com",
    priceRange: p.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: p.address?.street,
      addressLocality: p.address?.city || "New Braunfels",
      addressRegion: p.address?.region || "TX",
      postalCode: p.address?.postal,
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: p.geo.lat,
      longitude: p.geo.lng,
    },
    amenityFeature: p.amenities.map((a) => ({
      "@type": "LocationFeatureSpecification",
      name: a,
      value: true,
    })),
  };
};
